import { supabase } from '@/lib/supabaseClient';
import {
  calculateSettlement,
  roundToCentavos,
  SettlementCalculationInput,
  SettlementCalculationResult,
  ContractProfitSplit,
  PigArrivalItem,
  MortalityEventItem,
  FeedReceiptItem,
  FeedUsageItem,
  MedicineReceiptItem,
  HealthEventItem,
  ExpenseItem,
  PigSaleItem,
  SettlementAdjustmentItem,
  ReconciliationIssueItem,
} from '@/engine/settlement_calculator';
import {
  SettlementRecord,
  SettlementLineRecord,
  SettlementAdjustmentRecord,
  CreateSettlementAdjustmentPayload,
  GrowerContractRecord,
  RateRuleRecord,
  TransactionCategoryRecord,
  ProductionCycleRecord,
} from '@/types/database';

export interface SettlementFilters {
  status?: string; // 'all' | 'ready_to_settle' | 'draft' | 'approved' | 'frozen'
  growerId?: string;
  farmId?: string;
  search?: string;
}

export interface FreezeSettlementPayload {
  settlementId: string;
  cycleId: string;
  calculationResult: SettlementCalculationResult;
  adminOverride?: boolean;
  overrideReason?: string;
  notes?: string;
}

export interface SettlementWorkspaceData {
  cycle: ProductionCycleRecord;
  existingSettlement: SettlementRecord | null;
  contractSplit: ContractProfitSplit | null;
  availableContracts: GrowerContractRecord[];
  input: SettlementCalculationInput;
  calculationResult: SettlementCalculationResult;
  criticalIssues: any[];
  allAdjustments: SettlementAdjustmentRecord[];
}

export interface CycleSettlementSummary {
  settlement: SettlementRecord | null;
  calculationResult: SettlementCalculationResult | null;
  status: string;
  isFrozen: boolean;
  isCompanyOwned: boolean;
  total_sales_revenue: number;
  gross_profit: number;
  grower_share_pct: number;
  vsb_share_pct: number;
  grower_gross_share: number;
  grower_net_payout: number;
  total_production_expenses: number;
  settlement_number: string;
  settlement_date: string | null;
}

export const settlementService = {
  /**
   * Fetches all settlements with cycle, grower, and farm metadata.
   */
  async getSettlements(filters?: SettlementFilters): Promise<SettlementRecord[]> {
    try {
      let query = supabase
        .from('settlements')
        .select(`
          *,
          production_cycles:cycle_id(
            id, cycle_code, batch_code, status, start_date, expected_sale_date, actual_close_date,
            farms:farm_id(id, farm_name),
            growers:grower_id(id, full_name),
            production_units:production_unit_id(id, unit_code)
          ),
          growers:grower_id(id, full_name, contact_number),
          grower_contracts:contract_id(id, contract_number, vsb_share_pct, grower_share_pct)
        `)
        .eq('is_void', false)
        .order('settlement_date', { ascending: false });

      if (filters?.status && filters.status !== 'all' && filters.status !== 'ready_to_settle') {
        query = query.eq('status', filters.status);
      }

      if (filters?.growerId) {
        query = query.eq('grower_id', filters.growerId);
      }

      const { data, error } = await query;
      if (error) {
        console.error('[settlementService.getSettlements] Error:', error.message);
        throw error;
      }

      let results = (data || []) as SettlementRecord[];

      if (filters?.search) {
        const term = filters.search.toLowerCase();
        results = results.filter((s) => {
          const settlementNo = (s.settlement_number || '').toLowerCase();
          const cycleCode = (s.production_cycles?.cycle_code || '').toLowerCase();
          const growerName = (s.growers?.full_name || s.legacy_raiser_name || '').toLowerCase();
          const farmName = (s.production_cycles?.farms?.farm_name || '').toLowerCase();
          return (
            settlementNo.includes(term) ||
            cycleCode.includes(term) ||
            growerName.includes(term) ||
            farmName.includes(term)
          );
        });
      }

      return results;
    } catch (err: any) {
      console.error('[settlementService.getSettlements] Exception:', err.message);
      throw err;
    }
  },

  /**
   * Fetches cycles that are ready to settle (selling or closed without a frozen settlement).
   */
  async getCyclesReadyToSettle(): Promise<ProductionCycleRecord[]> {
    try {
      const { data, error } = await supabase
        .from('production_cycles')
        .select(`
          *,
          farms:farm_id(id, farm_name),
          growers:grower_id(id, full_name),
          production_units:production_unit_id(id, unit_code),
          grower_contracts:contract_id(id, contract_number, vsb_share_pct, grower_share_pct)
        `)
        .eq('is_void', false)
        .in('status', ['selling', 'settled', 'closed', 'active'])
        .order('cycle_code', { ascending: true });

      if (error) {
        console.error('[settlementService.getCyclesReadyToSettle] Error:', error.message);
        return [];
      }
      return (data || []) as ProductionCycleRecord[];
    } catch (err: any) {
      console.error('[settlementService.getCyclesReadyToSettle] Exception:', err.message);
      return [];
    }
  },

  /**
   * Fetches an existing settlement for a given cycle ID with lines and adjustments.
   */
  async getSettlementByCycleId(cycleId: string): Promise<SettlementRecord | null> {
    try {
      const { data, error } = await supabase
        .from('settlements')
        .select(`
          *,
          production_cycles:cycle_id(*, farms:farm_id(*), growers:grower_id(*), production_units:production_unit_id(*)),
          growers:grower_id(*),
          grower_contracts:contract_id(*),
          settlement_lines(*),
          settlement_adjustments(*, transaction_categories:category_id(*), rate_rules:rate_rule_id(*))
        `)
        .eq('cycle_id', cycleId)
        .eq('is_void', false)
        .maybeSingle();

      if (error) {
        console.error('[settlementService.getSettlementByCycleId] Error:', error.message);
        return null;
      }
      return (data as SettlementRecord) || null;
    } catch (err: any) {
      console.error('[settlementService.getSettlementByCycleId] Exception:', err.message);
      return null;
    }
  },

  /**
   * Fetches available active contracts for a grower.
   */
  async getContractsForGrower(growerId?: string | null): Promise<GrowerContractRecord[]> {
    try {
      let query = supabase
        .from('grower_contracts')
        .select('*')
        .eq('is_active', true);

      if (growerId) {
        query = query.eq('grower_id', growerId);
      }

      const { data, error } = await query.order('contract_number', { ascending: true });
      if (error) {
        console.error('[settlementService.getContractsForGrower] Error:', error.message);
        return [];
      }
      return (data || []) as GrowerContractRecord[];
    } catch (err: any) {
      console.error('[settlementService.getContractsForGrower] Exception:', err.message);
      return [];
    }
  },

  /**
   * Fetches unresolved critical reconciliation issues for a cycle.
   */
  async getUnresolvedCriticalIssues(cycleId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('reconciliation_issues')
        .select('*')
        .eq('cycle_id', cycleId)
        .eq('status', 'needs_review')
        .eq('severity', 'critical');

      if (error) {
        console.warn('[settlementService.getUnresolvedCriticalIssues] Error:', error.message);
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.warn('[settlementService.getUnresolvedCriticalIssues] Exception:', err.message);
      return [];
    }
  },

  /**
   * Queries rate_rules for suggested rates (e.g. Used Sacks 4020, Biogas 4040/4041).
   */
  async getRateRuleSuggestion(
    accountCode: string,
    date: string,
    farmId?: string | null,
    growerId?: string | null
  ): Promise<RateRuleRecord | null> {
    try {
      let categoryFilter = 'sack';
      if (accountCode === '4040' || accountCode === '4041') {
        categoryFilter = 'biogas';
      }

      const { data, error } = await supabase
        .from('rate_rules')
        .select('*')
        .eq('is_void', false)
        .eq('is_active', true)
        .eq('status', 'active')
        .ilike('rule_category', `%${categoryFilter}%`)
        .lte('effective_start_date', date);

      if (error || !data || data.length === 0) return null;

      const activeRules = (data as RateRuleRecord[]).filter((r) => {
        if (!r.effective_end_date) return true;
        return r.effective_end_date >= date;
      });

      if (activeRules.length === 0) return null;

      // Priority 1: Grower match
      if (growerId) {
        const gm = activeRules.find((r) => r.grower_id === growerId);
        if (gm) return gm;
      }

      // Priority 2: Farm match
      if (farmId) {
        const fm = activeRules.find((r) => r.farm_id === farmId);
        if (fm) return fm;
      }

      // Priority 3: Global
      return activeRules[0];
    } catch (err: any) {
      console.warn('[settlementService.getRateRuleSuggestion] Exception:', err.message);
      return null;
    }
  },

  /**
   * Fetches all Chart of Accounts transaction categories for adjustments (4010 - 4050).
   */
  async getAdjustmentCategories(): Promise<TransactionCategoryRecord[]> {
    try {
      const { data, error } = await supabase
        .from('transaction_categories')
        .select('*')
        .eq('is_void', false)
        .in('code', ['4010', '4020', '4030', '4031', '4040', '4041', '4050'])
        .order('code', { ascending: true });

      if (error) {
        console.error('[settlementService.getAdjustmentCategories] Error:', error.message);
        return [];
      }
      return (data || []) as TransactionCategoryRecord[];
    } catch (err: any) {
      console.error('[settlementService.getAdjustmentCategories] Exception:', err.message);
      return [];
    }
  },

  /**
   * Fetches all operational data for a cycle, builds the calculation input,
   * and runs the authoritative WP05 calculateSettlement calculation engine.
   */
  async getSettlementWorkspaceData(
    cycleId: string,
    overrideSplit?: ContractProfitSplit | null
  ): Promise<SettlementWorkspaceData> {
    // 1. Fetch Cycle Header with grower, farm, unit, contract
    const { data: cycle, error: cycleErr } = await supabase
      .from('production_cycles')
      .select(`
        *,
        farms:farm_id(*),
        growers:grower_id(*),
        production_units:production_unit_id(*),
        grower_contracts:contract_id(*)
      `)
      .eq('id', cycleId)
      .single();

    if (cycleErr || !cycle) {
      throw new Error(`Failed to load cycle with ID ${cycleId}: ${cycleErr?.message || 'Not found'}`);
    }

    // 2. Fetch Existing Settlement (if any)
    const existingSettlement = await this.getSettlementByCycleId(cycleId);

    // 3. Fetch Operational Datasets in Parallel
    const [
      arrivalsRes,
      mortalitiesRes,
      feedReceiptsRes,
      feedUsageRes,
      feedTransfersRes,
      medReceiptsRes,
      healthEventsRes,
      expensesRes,
      salesRes,
      adjustmentsRes,
      issuesRes,
      contractsRes,
    ] = await Promise.all([
      supabase.from('pig_arrivals').select('*').eq('cycle_id', cycleId).eq('is_void', false).order('arrival_date'),
      supabase.from('mortality_events').select('*').eq('cycle_id', cycleId).eq('is_void', false).order('event_date'),
      supabase.from('feed_receipts').select('*').eq('cycle_id', cycleId).eq('is_void', false).order('receipt_date'),
      supabase.from('feed_usage').select('*').eq('cycle_id', cycleId).eq('is_void', false).order('usage_date'),
      supabase.from('feed_transfers').select('*').or(`source_cycle_id.eq.${cycleId},destination_cycle_id.eq.${cycleId}`).eq('is_void', false).order('transfer_date'),
      supabase.from('medicine_receipts').select('*').eq('cycle_id', cycleId).eq('is_void', false).order('receipt_date'),
      supabase.from('health_events').select('*').eq('cycle_id', cycleId).eq('is_void', false).order('event_date'),
      supabase.from('expenses').select('*').eq('cycle_id', cycleId).eq('is_void', false).order('expense_date'),
      supabase.from('pig_sales').select('*').eq('cycle_id', cycleId).eq('is_void', false).order('sale_date'),
      supabase.from('settlement_adjustments').select('*, transaction_categories(*), rate_rules(*)').eq('cycle_id', cycleId).eq('is_void', false).order('adjustment_date'),
      supabase.from('reconciliation_issues').select('*').eq('cycle_id', cycleId),
      this.getContractsForGrower(cycle.grower_id),
    ]);

    const arrivals = arrivalsRes.data || [];
    const mortalities = mortalitiesRes.data || [];
    const feedReceipts = feedReceiptsRes.data || [];
    const feedUsage = feedUsageRes.data || [];
    const feedTransfers = feedTransfersRes.data || [];
    const medReceipts = medReceiptsRes.data || [];
    const healthEvents = healthEventsRes.data || [];
    const expenses = expensesRes.data || [];
    const sales = salesRes.data || [];
    const adjustments = (adjustmentsRes.data || []) as SettlementAdjustmentRecord[];
    const issues = issuesRes.data || [];
    const contracts = contractsRes || [];

    // 4. Determine Profit Split (Strict: No Silent Fallback)
    let contractSplit: ContractProfitSplit | null = null;
    if (overrideSplit) {
      contractSplit = overrideSplit;
    } else if (cycle.grower_contracts) {
      contractSplit = {
        vsb_share_pct: Number(cycle.grower_contracts.vsb_share_pct),
        grower_share_pct: Number(cycle.grower_contracts.grower_share_pct),
        source: 'grower_contract',
        contract_code: cycle.grower_contracts.contract_number || undefined,
      };
    } else if (existingSettlement?.grower_contracts) {
      contractSplit = {
        vsb_share_pct: Number(existingSettlement.grower_contracts.vsb_share_pct),
        grower_share_pct: Number(existingSettlement.grower_contracts.grower_share_pct),
        source: 'grower_contract',
        contract_code: existingSettlement.grower_contracts.contract_number || undefined,
      };
    } else if (existingSettlement && existingSettlement.vsb_share_pct !== undefined && existingSettlement.grower_share_pct !== undefined) {
      contractSplit = {
        vsb_share_pct: Number(existingSettlement.vsb_share_pct),
        grower_share_pct: Number(existingSettlement.grower_share_pct),
        source: 'historical_evidence',
      };
    }

    // 5. Transform Operational Records into Settlement Engine Input
    const pigArrivals: PigArrivalItem[] = arrivals.map((a) => ({
      id: a.id,
      arrival_date: a.arrival_date,
      head_count: a.head_count,
      total_weight_kg: a.total_weight_kg,
      base_amount: Number(a.base_amount || 0),
      excess_weight_amount: Number(a.excess_weight_amount || 0),
      trucking_cost: Number(a.trucking_cost || 0),
      total_cost: Number(a.total_cost || 0),
      notes: a.notes,
    }));

    const mortalityEvents: MortalityEventItem[] = mortalities.map((m) => ({
      id: m.id,
      event_date: m.event_date,
      head_count: m.head_count,
      total_weight_kg: m.total_weight_kg,
      cause_of_death: m.cause_of_death,
    }));

    const feedReceiptItems: FeedReceiptItem[] = feedReceipts.map((f) => ({
      id: f.id,
      receipt_date: f.receipt_date,
      quantity_bags: f.quantity_bags,
      total_weight_kg: f.total_weight_kg,
      unit_price: Number(f.unit_price || 0),
      feed_cost: Number(f.feed_cost || 0),
      feed_markup_amount: f.feed_markup_amount ? Number(f.feed_markup_amount) : null,
      trucking_cost: f.trucking_cost ? Number(f.trucking_cost) : null,
      total_cost: Number(f.total_cost || 0),
      is_direct_to_cycle: f.is_direct_to_cycle ?? true,
      cycle_id: f.cycle_id,
    }));

    const feedUsageItems: FeedUsageItem[] = feedUsage.map((u) => ({
      id: u.id,
      receipt_id: u.receipt_id,
      cycle_id: u.cycle_id,
      usage_date: u.usage_date,
      quantity_bags: u.quantity_bags,
      total_weight_kg: u.total_weight_kg,
      unit_cost: Number(u.unit_cost || 0),
      total_cost: Number(u.total_cost || 0),
    }));

    const medicineReceiptItems: MedicineReceiptItem[] = medReceipts.map((m) => ({
      id: m.id,
      receipt_date: m.receipt_date,
      quantity: m.quantity,
      unit_price: Number(m.unit_price || 0),
      total_amount: Number(m.total_amount || 0),
      account_code: m.account_code || '3030',
      category_id: m.category_id,
      description: m.notes || undefined,
    }));

    const healthEventItems: HealthEventItem[] = healthEvents.map((h) => ({
      id: h.id,
      event_date: h.event_date,
      event_type: h.event_type,
      head_count_treated: h.head_count_treated,
      total_cost: Number(h.total_cost || 0),
      diagnostic_result: h.diagnostic_result,
    }));

    const expenseItems: ExpenseItem[] = expenses.map((e) => ({
      id: e.id,
      expense_date: e.expense_date,
      amount: Number(e.amount || 0),
      account_code: e.account_code || '3050',
      category_id: e.category_id,
      description: e.description || '',
    }));

    const pigSaleItems: PigSaleItem[] = sales.map((s) => ({
      id: s.id,
      sale_date: s.sale_date,
      head_count: s.head_count,
      gross_weight_kg: Number(s.gross_weight_kg || 0),
      shrinkage_rate_applied: s.shrinkage_rate_applied ? Number(s.shrinkage_rate_applied) : null,
      shrinkage_deduction_kg: s.shrinkage_deduction_kg ? Number(s.shrinkage_deduction_kg) : null,
      net_weight_kg: Number(s.net_weight_kg || 0),
      price_per_kg: Number(s.price_per_kg || 0),
      total_amount: Number(s.total_amount || 0),
      scale_ticket_no: s.scale_ticket_no,
      account_code: '1010',
    }));

    const adjustmentItems: SettlementAdjustmentItem[] = adjustments.map((a) => ({
      id: a.id,
      adjustment_date: a.adjustment_date,
      account_code: a.account_code,
      adjustment_type: a.adjustment_type,
      amount: Number(a.amount || 0),
      quantity: a.quantity ? Number(a.quantity) : null,
      unit_rate: a.unit_rate ? Number(a.unit_rate) : null,
      description: a.description,
      rate_rule_id: a.rate_rule_id,
    }));

    const reconciliationIssueItems: ReconciliationIssueItem[] = issues.map((i) => ({
      id: i.id,
      issue_type: i.issue_type,
      severity: i.severity,
      status: i.status,
      description: i.description || '',
    }));

    const feedTransferItems: any[] = feedTransfers.map((t) => ({
      id: t.id,
      transfer_date: t.transfer_date,
      source_cycle_id: t.source_cycle_id,
      destination_cycle_id: t.destination_cycle_id,
      quantity_bags: Number(t.quantity_bags || 0),
      total_weight_kg: t.total_weight_kg ? Number(t.total_weight_kg) : null,
      unit_price: Number(t.unit_price || 0),
      total_transferred_cost: Number(t.total_transferred_cost || 0),
      notes: t.notes,
    }));

    const input: SettlementCalculationInput = {
      cycle: {
        id: cycle.id,
        cycle_code: cycle.cycle_code,
        batch_code: cycle.batch_code,
        grower_id: cycle.grower_id,
        grower_name: cycle.growers?.full_name || cycle.legacy_raiser_name,
        farm_id: cycle.farm_id,
        farm_name: cycle.farms?.farm_name,
        production_unit_id: cycle.production_unit_id,
        unit_code: cycle.production_units?.unit_code,
        contract_id: cycle.contract_id,
        status: cycle.status,
        start_date: cycle.start_date,
        expected_sale_date: cycle.expected_sale_date,
        actual_close_date: cycle.actual_close_date,
        legacy_raiser_name: cycle.legacy_raiser_name,
        legacy_batch_code: cycle.legacy_batch_code,
      },
      contractSplit,
      pigArrivals,
      mortalityEvents,
      feedReceipts: feedReceiptItems,
      feedUsage: feedUsageItems,
      feedTransfers: feedTransferItems,
      medicineReceipts: medicineReceiptItems,
      healthEvents: healthEventItems,
      expenses: expenseItems,
      pigSales: pigSaleItems,
      adjustments: adjustmentItems,
      reconciliationIssues: reconciliationIssueItems,
    };

    // 6. Execute Validated WP05 Settlement Engine (or load immutable snapshot if frozen)
    let calculationResult: SettlementCalculationResult;
    if (existingSettlement?.status === 'frozen' && existingSettlement.snapshot_data) {
      calculationResult = existingSettlement.snapshot_data as SettlementCalculationResult;
    } else {
      calculationResult = calculateSettlement(input);
    }

    const criticalIssues = issues.filter(
      (i) => i.severity === 'critical' && i.status === 'needs_review' && !i.is_void
    );

    return {
      cycle,
      existingSettlement,
      contractSplit,
      availableContracts: contracts,
      input,
      calculationResult,
      criticalIssues,
      allAdjustments: adjustments,
    };
  },

  /**
   * Fetches an authoritative single-source settlement summary for Cycle Detail Tab 8 and other overview views.
   * - If Company-Owned: identifies company-owned status (no grower settlement).
   * - If Frozen: displays stored immutable snapshot figures.
   * - If Draft / Live: dynamically computes live Draft figures using the single calculation engine.
   */
  async getCycleSettlementSummary(cycleId: string): Promise<CycleSettlementSummary | null> {
    try {
      const workspaceData = await this.getSettlementWorkspaceData(cycleId);
      const { cycle, existingSettlement, calculationResult } = workspaceData;

      const isCompanyOwned =
        cycle.operation_type === 'company_owned' ||
        Boolean((cycle as any).is_company_owned) ||
        Boolean((cycle.farms as any)?.is_company_owned);

      if (isCompanyOwned) {
        return {
          settlement: null,
          calculationResult: null,
          status: 'company_owned',
          isFrozen: false,
          isCompanyOwned: true,
          total_sales_revenue: 0,
          gross_profit: 0,
          grower_share_pct: 0,
          vsb_share_pct: 100,
          grower_gross_share: 0,
          grower_net_payout: 0,
          total_production_expenses: 0,
          settlement_number: '',
          settlement_date: null,
        };
      }

      const isFrozen = existingSettlement?.status === 'frozen';
      const status = existingSettlement?.status || 'draft';

      return {
        settlement: existingSettlement,
        calculationResult,
        status,
        isFrozen,
        isCompanyOwned: false,
        total_sales_revenue: calculationResult.total_sales_revenue,
        gross_profit: calculationResult.gross_profit,
        grower_share_pct: calculationResult.profit_split.grower_share_pct,
        vsb_share_pct: calculationResult.profit_split.vsb_share_pct,
        grower_gross_share: calculationResult.grower_gross_share,
        grower_net_payout: calculationResult.grower_net_payout,
        total_production_expenses: calculationResult.total_production_expenses,
        settlement_number: existingSettlement?.settlement_number || 'Draft Settlement',
        settlement_date: existingSettlement?.settlement_date || null,
      };
    } catch (err: any) {
      console.error('[settlementService.getCycleSettlementSummary] Error:', err.message);
      return null;
    }
  },

  /**
   * Saves or updates a draft settlement header.
   */
  async saveDraftSettlement(
    cycleId: string,
    growerId: string,
    contractId: string | null,
    calculationResult: SettlementCalculationResult,
    notes?: string
  ): Promise<{ success: boolean; settlementId?: string; error?: string }> {
    try {
      // Check if existing settlement exists
      const existing = await this.getSettlementByCycleId(cycleId);
      if (existing && existing.status === 'frozen') {
        return { success: false, error: 'Cannot modify a frozen settlement.' };
      }

      const settlementNumber = existing?.settlement_number || `STL-${new Date().getFullYear()}-${cycleId.substring(0, 8).toUpperCase()}`;

      const payload = {
        settlement_number: settlementNumber,
        cycle_id: cycleId,
        grower_id: growerId,
        contract_id: contractId,
        settlement_date: new Date().toISOString().split('T')[0],
        status: existing?.status === 'approved' ? 'approved' : 'draft',
        total_sales_revenue: calculationResult.total_sales_revenue,
        total_feed_cost: calculationResult.total_feed_cost,
        total_piglet_cost: calculationResult.total_piglet_cost,
        total_med_cost: calculationResult.total_med_cost,
        total_other_costs: calculationResult.total_other_costs,
        gross_profit: calculationResult.gross_profit,
        vsb_share_pct: calculationResult.profit_split.vsb_share_pct,
        grower_share_pct: calculationResult.profit_split.grower_share_pct,
        vsb_share_amount: calculationResult.vsb_share_amount,
        grower_gross_share: calculationResult.grower_gross_share,
        total_adjustments: calculationResult.net_adjustments,
        grower_net_payout: calculationResult.grower_net_payout,
        fcr: calculationResult.kpis.fcr,
        adg_kg_day: calculationResult.kpis.adg_kg_day,
        mortality_rate_pct: calculationResult.kpis.mortality_rate_pct,
        notes: notes || existing?.notes || null,
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        const { error } = await supabase
          .from('settlements')
          .update(payload)
          .eq('id', existing.id);

        if (error) throw error;
        return { success: true, settlementId: existing.id };
      } else {
        const { data, error } = await supabase
          .from('settlements')
          .insert({
            ...payload,
            created_at: new Date().toISOString(),
          })
          .select('id')
          .single();

        if (error) throw error;
        return { success: true, settlementId: data.id };
      }
    } catch (err: any) {
      console.error('[settlementService.saveDraftSettlement] Error:', err.message);
      return { success: false, error: err.message };
    }
  },

  /**
   * Approves a draft settlement via PostgreSQL RPC.
   */
  async approveSettlement(
    settlementId: string,
    cycleId: string,
    notes?: string,
    adminOverride: boolean = false,
    overrideReason?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('approve_settlement_atomic', {
        p_settlement_id: settlementId,
        p_cycle_id: cycleId,
        p_notes: notes || null,
        p_admin_override: adminOverride,
        p_override_reason: overrideReason || null,
      });

      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      console.error('[settlementService.approveSettlement] Error:', err.message);
      return { success: false, error: err.message };
    }
  },

  /**
   * Atomically freezes a settlement, writes itemized lines and snapshot data,
   * transitions settlement to 'frozen' and cycle to 'settled'.
   */
  async freezeSettlementAtomic(
    payload: FreezeSettlementPayload
  ): Promise<{ success: boolean; settlement_id?: string; error?: string }> {
    try {
      const { settlementId, cycleId, calculationResult, adminOverride, overrideReason, notes } = payload;

      const linesPayload = calculationResult.settlement_lines.map((l) => ({
        line_number: l.line_number,
        line_type: l.line_type,
        account_code: l.account_code,
        description: l.description,
        quantity: l.quantity,
        unit_measurement: l.unit_measurement,
        unit_price: l.unit_price,
        total_amount: l.total_amount,
        is_deduction: l.is_deduction,
        reference_table: l.reference_table,
        reference_id: l.reference_id,
        notes: null,
      }));

      const financialsPayload = {
        total_sales_revenue: calculationResult.total_sales_revenue,
        total_feed_cost: calculationResult.total_feed_cost,
        total_piglet_cost: calculationResult.total_piglet_cost,
        total_med_cost: calculationResult.total_med_cost,
        total_other_costs: calculationResult.total_other_costs,
        gross_profit: calculationResult.gross_profit,
        vsb_share_pct: calculationResult.profit_split.vsb_share_pct,
        grower_share_pct: calculationResult.profit_split.grower_share_pct,
        vsb_share_amount: calculationResult.vsb_share_amount,
        grower_gross_share: calculationResult.grower_gross_share,
        total_adjustments: calculationResult.net_adjustments,
        grower_net_payout: calculationResult.grower_net_payout,
      };

      const kpisPayload = {
        fcr: calculationResult.kpis.fcr,
        adg_kg_day: calculationResult.kpis.adg_kg_day,
        mortality_rate_pct: calculationResult.kpis.mortality_rate_pct,
      };

      const { data, error } = await supabase.rpc('freeze_settlement_atomic', {
        p_settlement_id: settlementId,
        p_cycle_id: cycleId,
        p_lines: linesPayload,
        p_snapshot: calculationResult.snapshot_data,
        p_financials: financialsPayload,
        p_kpis: kpisPayload,
        p_admin_override: !!adminOverride,
        p_override_reason: overrideReason || null,
        p_notes: notes || null,
      });

      if (error) throw error;
      return { success: true, settlement_id: data?.settlement_id };
    } catch (err: any) {
      console.error('[settlementService.freezeSettlementAtomic] Error:', err.message);
      return { success: false, error: err.message };
    }
  },

  /**
   * Creates a new settlement adjustment row (e.g. 4010-4050).
   */
  async createSettlementAdjustment(
    payload: CreateSettlementAdjustmentPayload
  ): Promise<{ success: boolean; adjustment?: SettlementAdjustmentRecord; error?: string }> {
    try {
      // 1. Verify parent settlement is not frozen
      const settlement = await this.getSettlementByCycleId(payload.cycle_id);
      if (settlement && settlement.status === 'frozen') {
        return { success: false, error: 'Cannot add adjustments to a frozen settlement.' };
      }

      // 2. Look up category_id for account_code if not provided
      let categoryId = payload.category_id;
      if (!categoryId) {
        const { data: cat } = await supabase
          .from('transaction_categories')
          .select('id')
          .eq('code', payload.account_code)
          .eq('is_void', false)
          .maybeSingle();
        if (cat) categoryId = cat.id;
      }

      if (!categoryId) {
        return { success: false, error: `Invalid account code ${payload.account_code}. Category not found.` };
      }

      const adjustmentNumber = `ADJ-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;

      const { data, error } = await supabase
        .from('settlement_adjustments')
        .insert({
          adjustment_number: adjustmentNumber,
          settlement_id: payload.settlement_id,
          cycle_id: payload.cycle_id,
          category_id: categoryId,
          account_code: payload.account_code,
          adjustment_type: payload.adjustment_type,
          adjustment_date: payload.adjustment_date,
          amount: roundToCentavos(payload.amount),
          quantity: payload.quantity ? Number(payload.quantity) : null,
          unit_rate: payload.unit_rate ? Number(payload.unit_rate) : null,
          rate_rule_id: payload.rate_rule_id || null,
          description: payload.description,
          notes: payload.notes || null,
        })
        .select('*, transaction_categories(*), rate_rules(*)')
        .single();

      if (error) throw error;
      return { success: true, adjustment: data as SettlementAdjustmentRecord };
    } catch (err: any) {
      console.error('[settlementService.createSettlementAdjustment] Error:', err.message);
      return { success: false, error: err.message };
    }
  },

  /**
   * Updates an existing settlement adjustment row.
   */
  async updateSettlementAdjustment(
    id: string,
    payload: Partial<CreateSettlementAdjustmentPayload>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };
      if (payload.amount !== undefined) updateData.amount = roundToCentavos(payload.amount);
      if (payload.quantity !== undefined) updateData.quantity = payload.quantity ? Number(payload.quantity) : null;
      if (payload.unit_rate !== undefined) updateData.unit_rate = payload.unit_rate ? Number(payload.unit_rate) : null;
      if (payload.description !== undefined) updateData.description = payload.description;
      if (payload.adjustment_date !== undefined) updateData.adjustment_date = payload.adjustment_date;
      if (payload.notes !== undefined) updateData.notes = payload.notes;

      const { error } = await supabase
        .from('settlement_adjustments')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      console.error('[settlementService.updateSettlementAdjustment] Error:', err.message);
      return { success: false, error: err.message };
    }
  },

  /**
   * Soft-voids a settlement adjustment.
   */
  async voidSettlementAdjustment(
    id: string,
    reason: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('settlement_adjustments')
        .update({
          is_void: true,
          void_reason: reason,
          voided_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      console.error('[settlementService.voidSettlementAdjustment] Error:', err.message);
      return { success: false, error: err.message };
    }
  },
};
